"use client";

import type * as React from "react";

import { cn } from "@rivval/css";

const variants = {
  primary: {
    border: "before:border-cyan-400 after:border-cyan-400",
    text: "text-cyan-400",
  },
  secondary: {
    border: "before:border-violet-400 after:border-violet-400",
    text: "text-violet-400",
  },
  success: {
    border: "before:border-lime-400 after:border-lime-400",
    text: "text-lime-400",
  },
  warning: {
    border: "before:border-yellow-400 after:border-yellow-400",
    text: "text-yellow-400",
  },
  destructive: {
    border: "before:border-red-400 after:border-red-400",
    text: "text-red-400",
  },
  info: {
    border: "before:border-blue-400 after:border-blue-400",
    text: "text-blue-400",
  },
} as const;

interface RazorButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  className?: string;
  variant?: keyof typeof variants;
  icon?: React.ReactNode;
  iconPosition?: "start" | "end";
}

export function RazorButton({
  children,
  className,
  variant = "primary",
  icon,
  iconPosition = "end",
  ...props
}: RazorButtonProps) {
  const variantStyles = variants[variant];

  return (
    <button
      className={cn(
        "group relative bg-transparent px-8 py-3",
        "before:absolute before:inset-0 before:border-2",
        "before:translate-x-1 before:translate-y-1",
        "after:absolute after:inset-0 after:border-2",
        "after:-translate-x-1 after:-translate-y-1",
        "hover:before:translate-x-0 hover:before:translate-y-0",
        "hover:after:translate-x-0 hover:after:translate-y-0",
        "before:transition-transform after:transition-transform",
        "before:duration-300 after:duration-300",
        variantStyles.border,
        className,
      )}
      {...props}
    >
      <span
        className={cn(
          "relative z-10 flex items-center gap-2 font-mono text-xl tracking-wider",
          variantStyles.text,
        )}
      >
        {icon && iconPosition === "start" && (
          <span className="inline-flex">{icon}</span>
        )}
        {children}
        {icon && iconPosition === "end" && (
          <span className="inline-flex">{icon}</span>
        )}
      </span>
      {/* Left cutout */}
      <div className="absolute left-0 top-1/2 h-4 w-2 -translate-x-1 -translate-y-1/2 bg-black" />
      {/* Right cutout */}
      <div className="absolute right-0 top-1/2 h-4 w-2 -translate-y-1/2 translate-x-1 bg-black" />
    </button>
  );
}
